import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { getPassagesBeforeNextAttempt } from "../../util/src/group-util";
import dico from "../../util/src/language/dico";
import type { AthletesModel } from "../../util/src/types";
import { resolveGroupCompetitionFormat } from "../../util/src/competition-format-util";
import AthleteRow from "./ReadOnlyRankingTable/AthleteRow";
import CategoryRow from "./ReadOnlyRankingTable/CategoryRow";
import {
  useCategoryColors,
  useCollapsedCategories,
  useCurrentLift,
  useGroupedAthletes,
  useSortedAthletes,
  useTableScroll,
} from "./ReadOnlyRankingTable/hooks";
import {
  StyledTable,
  TableBody,
  TableContainer,
  TableHeader,
} from "./ReadOnlyRankingTable/styled";
import TableCell from "./ReadOnlyRankingTable/TableCell";
import { ReadOnlyRankingTableProps } from "./ReadOnlyRankingTable/types";
import { useTableColumns } from "./ReadOnlyRankingTable/useTableColumns";

/**
 * ReadOnlyRankingTable - A comprehensive table component for displaying athlete rankings
 * with support for different lift phases, category grouping, and live weight sorting
 */
const ReadOnlyRankingTable = ({
  currentGroup,
  currentAthlete,
  records,
  maxHeight,
  colors,
  sortByLiveWeight = true,
  onAthleteClick,
  levels,
  athletes,
  onHypotheticalRankClick,
  defaultLevelName = null,
  pointsFormula = null,
  isNational = false,
}: ReadOnlyRankingTableProps) => {
  // Custom hooks for managing component state and logic
  const sortedAthletes = useSortedAthletes(currentGroup, sortByLiveWeight);
  const { currentLift, currentLiftType } = useCurrentLift(currentGroup);
  const passagesBefore = useMemo(
    () =>
      getPassagesBeforeNextAttempt(currentGroup?.athletes ?? [], currentLift),
    [currentGroup?.athletes, currentLift],
  );
  const categoryColors = useCategoryColors(sortedAthletes);
  const { collapsedCategories, toggleCategory } = useCollapsedCategories();
  const groupedAthletes = useGroupedAthletes(sortedAthletes, sortByLiveWeight);
  const tableContainerRef = useTableScroll();
  // DC/BP: hide squat / deadlift / total annoncé even if group format defaulted to FULL_POWER
  const competitionFormat = useMemo(
    () => resolveGroupCompetitionFormat(currentGroup),
    [currentGroup],
  );
  const rankingColumns = useTableColumns(
    currentLiftType,
    records,
    colors,
    competitionFormat,
    levels,
    athletes,
    onHypotheticalRankClick,
    defaultLevelName,
    pointsFormula,
    isNational,
  );
  const columns = [
    {
      id: "passagesBefore",
      header: dico().ranking.passagesBefore,
      flex: 1.2,
      minWidth: 210,
      align: "center" as const,
      render: (athlete: AthletesModel) => (
        <Typography fontWeight="bold">
          {dico().ranking.passageStatus(passagesBefore[athlete.id] ?? null)}
        </Typography>
      ),
    },
    ...rankingColumns,
  ];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        ref={tableContainerRef}
        sx={{
          flex: 1,
          maxHeight: maxHeight || "calc(100vh - 2z00px)",
          "@keyframes blink": {
            "0%": { opacity: 0.3 },
            "50%": { opacity: 1 },
            "100%": { opacity: 0.3 },
          },
          "&::-webkit-scrollbar": {
            height: "8px",
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "rgba(0,0,0,0.05)",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.2)",
            borderRadius: "4px",
          },
          overflow: "auto",
          touchAction: "pan-x pan-y",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          overscrollBehaviorY: "contain",
          // Removed scrollSnapType to prevent sticky jitter
          "& > div": {
            sx: {
              userSelect: "none",
            },
          },
          "@supports (-webkit-touch-callout: none)": {
            WebkitTapHighlightColor: "transparent",
          },
        }}
      >
        <StyledTable
          style={
            {
              // Minimal styling to prevent sticky conflicts
            }
          }
        >
          {/* Table Header */}
          <TableHeader>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                colors={colors}
                frozen={column.frozen}
                isHeader={true}
                flex={column.flex}
                minWidth={column.minWidth}
                align={column.align}
                categorycolor={undefined}
                sx={{
                  textAlign: column.align || "left",
                  justifyContent:
                    column.align === "center" ? "center" : "flex-start",
                }}
              >
                <Typography
                  sx={{
                    fontSize: {
                      xs: "0.7rem",
                      sm: "0.8rem",
                      md: "0.9rem",
                      lg: "1.2rem",
                    },
                    fontWeight: "bold",
                    textAlign: column.align || "left",
                    width: "100%",
                    color: "inherit",
                  }}
                >
                  {column.header}
                </Typography>
              </TableCell>
            ))}
          </TableHeader>

          {/* Table Body */}
          <TableBody>
            {sortByLiveWeight ? (
              // Live weight mode - simple display without separators
              sortedAthletes.map((athlete) => {
                const categoryKey = `${athlete.catAge?.nom || ""}|${athlete.catPoid?.nom || ""}|${athlete.discipline?.code || ""}`;
                const isCurrentAthlete = athlete.id === currentAthlete?.id;
                const categoryColor = categoryColors[categoryKey];

                return (
                  <AthleteRow
                    key={athlete.id}
                    athlete={athlete}
                    columns={columns}
                    isCurrentAthlete={isCurrentAthlete}
                    categoryColor={categoryColor}
                    colors={colors}
                    onRowClick={onAthleteClick}
                  />
                );
              })
            ) : (
              // Category mode - display with separators and collapse/expand functionality
              <Box sx={{ width: "100%" }}>
                {Object.entries(groupedAthletes).map(
                  ([categoryKey, athletes]) => {
                    const isCollapsed =
                      collapsedCategories[categoryKey] || false;
                    const categoryColor = categoryColors[categoryKey];

                    return (
                      <CategoryRow
                        AthleteRowComponent={AthleteRow}
                        key={categoryKey}
                        categoryKey={categoryKey}
                        athletes={athletes}
                        isCollapsed={isCollapsed}
                        onToggle={toggleCategory}
                        categoryColor={categoryColor}
                        columns={columns}
                        currentAthlete={currentAthlete}
                        colors={colors}
                        onAthleteClick={onAthleteClick}
                      />
                    );
                  },
                )}
              </Box>
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Box>
  );
};

export default ReadOnlyRankingTable;
